/* Dollar Labs B2B — "next volume tier" prompt for the cart and cart drawer.
 *
 * Reads tier data through the SDK the Collection Pricing app embed installs
 * (window.DollarlabsGetNextTierWarning), so there is no second copy of the
 * pricing rules here and no extra Storefront API traffic.
 *
 * The theme re-renders cart lines by fetching ?section_id=helper-cart and
 * replacing innerHTML, which destroys our injected nodes — hence the observer
 * rather than a one-shot pass on load.
 */
(function () {
  "use strict";

  var CONFIG_ID = "dl-tier-config";
  var PLACEHOLDER = "[data-dl-tier-prompt]";
  var API = "DollarlabsGetNextTierWarning";

  var config = readConfig();
  if (!config || !config.enabled) return;

  // Rising counter invalidates in-flight lookups: quantity can change twice
  // before the first await resolves, and the older answer must not win.
  var generation = 0;

  function readConfig() {
    var el = document.getElementById(CONFIG_ID);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  // The SDK arrives on a deferred script, so it may not exist yet on first paint.
  function waitForApi() {
    if (typeof window[API] === "function") return Promise.resolve(true);
    return new Promise(function (resolve) {
      var waited = 0;
      var tick = setInterval(function () {
        if (typeof window[API] === "function") {
          clearInterval(tick);
          resolve(true);
        } else if ((waited += 100) >= 5000) {
          clearInterval(tick);
          resolve(false);
        }
      }, 100);
    });
  }

  function currency() {
    return (
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
      "USD"
    );
  }

  // Only used for the aggregate savings figure; per-unit prices come back from
  // the SDK already formatted in the shop's own money format.
  function formatMoney(amount) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: currency(),
      }).format(amount);
    } catch (e) {
      return amount.toFixed(2);
    }
  }

  function fillTemplate(template, warning) {
    return String(template)
      .replace(/\[qty\]/g, String(warning.itemsNeeded))
      .replace(/\[price\]/g, warning.nextTierPriceFormatted)
      .replace(/\[percent\]/g, String(warning.savingsPercentage) + "%")
      .replace(/\[tier\]/g, String(warning.nextTierQuantity));
  }

  function clear(el) {
    el.textContent = "";
    el.hidden = true;
  }

  function render(el, warning) {
    var text = fillTemplate(config.message, warning);

    var inner = document.createElement("span");
    inner.className = "dl-tier-prompt__inner";

    var main = document.createElement("span");
    main.className = "dl-tier-prompt__text";
    main.textContent = text;
    inner.appendChild(main);

    // savingsPerUnit is in display currency, not cents.
    if (config.showSavings && warning.savingsPerUnit > 0) {
      var total = warning.savingsPerUnit * warning.nextTierQuantity;
      var savings = document.createElement("span");
      savings.className = "dl-tier-prompt__savings";
      savings.textContent = "save " + formatMoney(total);
      inner.appendChild(savings);
    }

    el.textContent = "";
    el.appendChild(inner);
    el.hidden = false;
    el.setAttribute("aria-live", "polite");
  }

  function update(el, token) {
    var variantId = el.getAttribute("data-variant-id");
    var quantity = parseInt(el.getAttribute("data-quantity"), 10);
    if (!variantId || !quantity || quantity < 1) return clear(el);

    // Skip work when neither input moved since the last successful render.
    var signature = variantId + ":" + quantity;
    if (el.dataset.dlSignature === signature) return;

    return Promise.resolve(window[API](variantId, quantity))
      .then(function (warning) {
        if (token !== generation) return; // a newer pass has superseded this one
        el.dataset.dlSignature = signature;
        if (warning && warning.itemsNeeded > 0) render(el, warning);
        else clear(el);
      })
      .catch(function () {
        if (token === generation) clear(el);
      });
  }

  function scan(root) {
    var nodes = (root || document).querySelectorAll(PLACEHOLDER);
    if (!nodes.length) return;
    var token = ++generation;
    Array.prototype.forEach.call(nodes, function (el) {
      update(el, token);
    });
  }

  function observe() {
    var timer;
    var schedule = function () {
      clearTimeout(timer);
      timer = setTimeout(function () {
        scan(document);
      }, 150);
    };

    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var r = records[i];
        // Our own writes mutate inside a placeholder; ignore them or the
        // observer feeds itself.
        if (
          r.target &&
          r.target.closest &&
          r.target.closest(PLACEHOLDER)
        )
          continue;
        schedule();
        return;
      }
    }).observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["data-quantity", "data-qty"],
    });

    // Belt and braces for themes/apps that announce cart changes explicitly.
    ["cart:updated", "cart:refresh", "shopify:section:load"].forEach(function (evt) {
      document.addEventListener(evt, schedule);
    });
  }

  function start() {
    waitForApi().then(function (ready) {
      if (!ready) return;
      scan(document);
      if (document.body) observe();
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();
